import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useRequestExportBiRun,
  useRetrieveExportBiRun,
} from "@planzone/view-models";
import { ExportBiRunStatus } from "@planzone/dto";

import { Checkbox } from "@common/checkbox";
import { Button } from "@common/button";
import { DateTimeInput } from "@common/dateTimeInput";
import { Props } from "./types";
import styles from "./styles.scss";

const POLLING_DELAY = 5000;

function toIsoDate(dateTime: string): string {
  return DateTime.fromISO(dateTime).toISODate() ?? "";
}

function BiExportReport({ planzone }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isRequestPending, requestExportBiRun } = useRequestExportBiRun();
  const { run, retrieveExportBiRun } = useRetrieveExportBiRun();
  const [isChecked, setIsChecked] = useState(run !== null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const isRunInProgress =
    run !== null &&
    (run.status === ExportBiRunStatus.PENDING ||
      run.status === ExportBiRunStatus.RUNNING);
  const hasGeneratedDocuments =
    !isRunInProgress &&
    !!run?.result &&
    Object.keys(run.result.documents).length > 0;
  const isRangeInvalid =
    !!startDate && !!endDate && toIsoDate(startDate) > toIsoDate(endDate);

  useEffect(() => {
    run && setIsChecked(true);
  }, [run?.runId]);

  useEffect(() => {
    if (!run || !isRunInProgress) return;
    const timeout = setTimeout(
      () => retrieveExportBiRun({ planzone, runId: run.runId }),
      POLLING_DELAY,
    );
    return () => clearTimeout(timeout);
  }, [run, isRunInProgress]);

  return (
    <div
      className={classNames(styles.report, {
        [styles.checked]: isChecked,
      })}
    >
      <div className={styles.row}>
        <div
          className={styles.checkbox}
          onClick={() => setIsChecked(!isChecked)}
        >
          <Checkbox isChecked={isChecked} />
        </div>
        <div className={styles.col}>
          <h2 className={styles.name}>
            {t("exportReportsPage.biExportReport.title")}
          </h2>
          <p className={styles.description}>
            {t("exportReportsPage.biExportReport.subTitle")}
          </p>
        </div>
        {isChecked && (
          <Button
            className={styles.button}
            type="submit"
            status={isRequestPending || isRunInProgress ? "pending" : "none"}
            isDisabled={!startDate || !endDate || isRangeInvalid}
            press={() =>
              requestExportBiRun({
                planzone,
                startDate: toIsoDate(startDate),
                endDate: toIsoDate(endDate),
              })
            }
          >
            {t("exportReportsPage.biExportReport.launch")}
          </Button>
        )}
      </div>
      {isChecked && (
        <div className={styles.filters}>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.biExportReport.startDate")}
            </h3>
            <DateTimeInput
              dateTime={startDate}
              placeholder={t("exportReportsPage.biExportReport.startDate")}
              isInvalid={isRangeInvalid}
              input={setStartDate}
            />
          </div>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.biExportReport.endDate")}
            </h3>
            <DateTimeInput
              dateTime={endDate}
              placeholder={t("exportReportsPage.biExportReport.endDate")}
              isInvalid={isRangeInvalid}
              input={setEndDate}
            />
          </div>
        </div>
      )}
      {isChecked && isRangeInvalid && (
        <p className={styles.error}>
          {t("exportReportsPage.biExportReport.invalidRange")}
        </p>
      )}
      {isChecked && hasGeneratedDocuments && (
        <span className={styles.link} onClick={() => navigate("/documents")}>
          {t("exportReportsPage.biExportReport.openDocuments")}
        </span>
      )}
    </div>
  );
}

export { BiExportReport };
